/**
 * Units PlanChangeFlow switched off for a smaller plan, remembered for this
 * browser session — so that, if the host then cancels Stripe Checkout, comes
 * back from it, or cancels the scheduled change, the app can offer to switch
 * them back on. sessionStorage only (a per-tab convenience, never the source
 * of truth); keyed by user, expires after 24 h, cleared on sign-out.
 */
const PREFIX = 'bookiapp:flow-deactivated-units';
const TTL_MS = 24 * 60 * 60 * 1000;

interface Stored {
  ids: string[];
  at: number;
}

const keyFor = (userId: string) => `${PREFIX}:${userId}`;

export function rememberDeactivatedUnits(userId: string | null | undefined, ids: string[]): void {
  if (!userId || !ids.length) return;
  try {
    const merged = Array.from(new Set([...readDeactivatedUnits(userId), ...ids]));
    sessionStorage.setItem(keyFor(userId), JSON.stringify({ ids: merged, at: Date.now() } satisfies Stored));
  } catch {
    // storage blocked — the offer simply won't appear
  }
}

export function readDeactivatedUnits(userId: string | null | undefined, now: number = Date.now()): string[] {
  if (!userId) return [];
  try {
    const raw = sessionStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (typeof parsed.at !== 'number' || now - parsed.at > TTL_MS) {
      sessionStorage.removeItem(keyFor(userId));
      return [];
    }
    return Array.isArray(parsed.ids) ? parsed.ids.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Keep only the ids that are still switched off. */
export function keepDeactivatedUnits(userId: string | null | undefined, ids: string[]): void {
  if (!userId) return;
  if (!ids.length) {
    forgetDeactivatedUnits(userId);
    return;
  }
  try {
    sessionStorage.setItem(keyFor(userId), JSON.stringify({ ids, at: Date.now() } satisfies Stored));
  } catch {
    // ignore
  }
}

export function forgetDeactivatedUnits(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    sessionStorage.removeItem(keyFor(userId));
  } catch {
    // ignore
  }
}

/** Sign-out: drop every user's entry in this tab. */
export function forgetAllDeactivatedUnits(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}
